import * as constants from "./game-information-constants";
import * as sim from "./simulator";
export let simx = sim;

export interface Player {
  id: number;
  estate: string;
  role: string;
  [key: string]: any;
}

export interface GameRoom {
  currentYear: number,
  joinedPlayers?: Set<number>,
  groups: Map<string, RoleGroup>, // keyed by string `${estate}-${role}`
  proposal: Proposal,
  proposalHistory: Proposal[],
  gameState: GameState,
  gameStateHistory: GameState[],
  wmVotes: Map<number, number>, // key is the player id, value is their vote
  aggCostHistory: any[],
  scoreHistory: any[],
}

export interface ChangeInHerdEvents {
  authorId: number,
  authorRole: string,
  authorEstate: string,
  quantity: number, // > 0 is a buy < 0 is a sell
}

// Get data from
//  startingRoleData(roles)
//
// This game data gets modified by users in game
// to create `proposal`
//
// send proposal to simulator
// get new map data
//
// process proposal to get new role data
//
// repeat cycle
export interface RoleGroup {
  money: number,
  startingRoleData: any, // Initial starting role data gained from game-info-constants (inventory, bank, available actions, etc)
  totalHuntingBag:number,// hunting bag starts at zero
  herdSize:number,
  subsidy:number, // yearly government subsidy
}
export interface MoneyTransferEvent {
  authorId: number,
  fromGroupName: string,
  toGroupName: string, // groups map key `${estate}-${role}`
  amountTransfer: number,
}
interface MapActions {
  authorId: number,
  authorRole: string,
  authorEstateClass: string,
  actionKind: string,
  location: {
    x: number,
    y: number,
  },
}
interface MuActions {
  authorId: number,
  actionKind: string,
  authorRole: string,
  authorEstateClass: string,
  managementUnit: number,
  wmRegime?: number[],
}
interface FaunaActions {
  faunaName: string,
  location: {
    x: number,
    y: number,
  },
  playerId: number,
  playerEstate: string,
  playerRole: string,

}

// the game state of the grid etc
export interface GameState{
  cellData: any[];
  muData: any[];
  gridData: sim.GridData;
  simData: sim.SimData;
}
// actions being taken
interface GameActions{
  // [ACTION, targetx, targety, playerid]
  cellActions:Array<[number, number, number, number]>;
  // ["FAUNANAME", X, Y, playerid]
  faunaActions:Array<[string, number, number, number]>;
  // ["ACTION", MU, WMREGIME (if applicable)]
  muActions:Array<[number, number, number[]|null]>;
  // overall regime
  wmRegime:number[];
}

export interface Proposal {
  moneyTransfers: Array<MoneyTransferEvent>,
  faunaActions: Array<FaunaActions>,
  mapActions: Array<MapActions>,
  muActions: Array<MuActions>,
  changeInHerdEvents: ChangeInHerdEvents[],
  reservationMuActions: Array<MuActions>,
}

export interface BalanceChange {
  balance:number,
  description:string,
}

export function balanceChange(balance:number, description:string){
  let bc: BalanceChange = {
    balance:balance,
    description:description,
  };
  return bc;
}

export interface MUPartialBalances{
  balanceFromHunting:number,
  huntedCells:number,
  balanceFromCutting:number,
  cutCells:number,
  balanceFromClearing:number,
  clearedCells:number,
  grazedValue:number[],
  grazedCells:number,
  ducksHunted:number,

}

export interface AggrigateCosts{
  role: string,
  estate: string,
  startingBalance: number,
  totalBalanceChange: number, // total difference in money. Plus is a credit and minus is a debt.
  metaBalanceChanges: BalanceChange[], // a list of all meta balance changes. This includes money transfers, public hunter revenue, buying and selling cows, cow revenue, cow grazing costs, and all static costs
  MUBalanceChanges: BalanceChange[][], // balance changes within an MU. This includes private hunting revenue, cutting revenue, and clearing costs.
  huntingBag:number,
  fodderCost:number,
  newHerdSize:number,
}

export function getAggrigateCosts(proposal: Proposal, gameState: GameState, wlRegime: number[], roleGroup: RoleGroup, estate:string, role:string, faunaClimateVariability = sim.FAUNA_CLIMATE_VARIABILITY[2], floraClimateVariability = sim.FLORA_CLIMATE_VARIABILITY[2], skipStaticCosts:boolean = false) : AggrigateCosts{
  console.log("PROPOSAL : ", proposal);
  let groupName = `${estate}-${role}`
  let metaTransfer: BalanceChange[] = [];
  // deal with direct transfers of wealth 
  if(roleGroup.subsidy > 0){
    metaTransfer.push(balanceChange(roleGroup.subsidy,"Recieved subsidy from government"));
  }
  console.log("PROPOSAL : " + proposal);
  for(let mte of proposal.moneyTransfers){
    if(mte.toGroupName === groupName){
      metaTransfer.push(balanceChange(mte.amountTransfer,"Recieved money from " + mte.fromGroupName));
      console.log("MONEY TRANSFER EVENT");
    }
    else if (mte.fromGroupName === groupName){
      metaTransfer.push(balanceChange(-mte.amountTransfer,"Sent money to " + mte.toGroupName));
    }
  }
  let newHerdSize = roleGroup.herdSize;
  // deal with the buying and selling of cows
  for(let che of proposal.changeInHerdEvents){
    if(che.authorEstate == estate && che.authorRole == role){
      let valuePerAnimal = constants.getRoleConstants(role, estate)!.animalValue || 0; // if there is no animal value it is free i guess. this occurs if a non stockbreeder buys an animal somehow.
      let cowCost = che.quantity * valuePerAnimal;
      let balanceText = ``;
      if(che.quantity > 0){
        balanceText = `Bought ${che.quantity} cows.`
      }
      else{
        balanceText = `Sold ${che.quantity} cows.`
      }
      newHerdSize += che.quantity // update the number of cows so they produce value etc
      metaTransfer.push(balanceChange(-cowCost, balanceText));
    }
  }

  // TODO: calculate that static costs for the roles, based on the data from the constants. 
  let staticCosts = constants.getRoleConstants(role, estate)?.staticCosts || [];
  for(let cost of staticCosts){
    if(!skipStaticCosts){
      metaTransfer.push(balanceChange(-cost[0], "" + cost[1]));
    }
  }

  let muPartialBalances:MUPartialBalances[] = []; // for calculation purposes
  let muBalances : BalanceChange[][]= [];
  for(let i = 0; i < gameState.muData.length; i++){
    let b : MUPartialBalances = {
      balanceFromHunting:0,
      huntedCells:0,
      balanceFromCutting:0,
      cutCells:0,
      balanceFromClearing:0,
      clearedCells:0,
      grazedCells:0,
      grazedValue:[0,0,0,0],
      ducksHunted:0,
    }
    muPartialBalances.push(b);
    muBalances.push([]); // empty array to hold balance changes
  }

  let totalHuntingBag = 0;

  // deal with the profits from hunting, public and private both
  for(let hue of proposal.faunaActions){
    if(hue.playerEstate === estate && hue.playerRole === role){
      let huntedFauna = getHuntingBag(hue.location.x, hue.location.y, gameState, wlRegime, faunaClimateVariability, hue.faunaName);
      let privateProfit = getPrivateHunterCellProfit(hue.location.x, hue.location.y, gameState, wlRegime, faunaClimateVariability, hue.faunaName);
      totalHuntingBag += huntedFauna;
      if(estate === "private" && role === "Hunter"){
        let cellMu = gameState.cellData[hue.location.x][hue.location.y].managementUnit;
        
        muPartialBalances[cellMu]!.balanceFromHunting += privateProfit;
        muPartialBalances[cellMu]!.huntedCells++;
        muPartialBalances[cellMu]!.ducksHunted += huntedFauna;
      }
    }
  }
  if(estate === "public" && role === "Hunter"){
    let phProfit = getPublicHunterProfit(totalHuntingBag);
    metaTransfer.push(balanceChange(phProfit, `Annual subsidy for hunting ${totalHuntingBag} ducks`));
  }
  // roleGroup.totalHuntingBag+= totalHuntingBag; // for calculating end-game score (dont do this here)
  console.log("THIS YEAR HUNTING BAG : ", totalHuntingBag);
  for(let mapev of proposal.mapActions){
    if(mapev.authorRole === role && mapev.authorEstateClass === estate){
      let cellMu = gameState.cellData[mapev.location.x][mapev.location.y].managementUnit;
      // here we only really need to worry about cutting and clearing tbh
      
      if(mapev.actionKind.toLowerCase() === "cut"){ // tolowercase because im unsure how its formatted
        let cutRev:number = getCellActionCost(sim.CUT, mapev.location.x, mapev.location.y, gameState, wlRegime, floraClimateVariability) || 0;
        muPartialBalances[cellMu].balanceFromCutting += cutRev * -1; // invert cuz of how the getCellActionCost func works
        muPartialBalances[cellMu].cutCells ++;
      }
      else if(mapev.actionKind.toLowerCase() === "clearing"){
        
        let clearCost:number = getCellActionCost(sim.CLEARING, mapev.location.x, mapev.location.y, gameState, wlRegime, floraClimateVariability) || 0;
        muPartialBalances[cellMu].balanceFromClearing += clearCost * -1; // invert cuz of how the getCellActionCost func works
        muPartialBalances[cellMu].clearedCells ++;
      }
      else if(mapev.actionKind.toLowerCase() === "grazing"){
        let graze = getGrazingValue(mapev.location.x, mapev.location.y, gameState, wlRegime, floraClimateVariability);
        muPartialBalances[cellMu].clearedCells ++;
        // sum the grazed cells
        for(let i = 0; i < graze.length; i++){
          
          muPartialBalances[cellMu].grazedValue[i] += graze[i];
        }

      }
    }
  }
  // mu actions when assembling mus

  for(let muact of proposal.muActions){
    if(muact.authorRole === role && muact.authorEstateClass === estate){
      if(muact.actionKind === "embank"){
        let cost = getEmbankmentCost(muact.managementUnit, gameState);
        muBalances[muact.managementUnit].push(balanceChange(-cost, "Embanked MU #" + muact.managementUnit));
      }
      else if(muact.actionKind === "disembank"){
        let cost = getDisembankmentCost(muact.managementUnit, gameState);
        muBalances[muact.managementUnit].push(balanceChange(-cost, "Disembanked MU #" + muact.managementUnit));
      }
    }
  }
  for(let resact of proposal.reservationMuActions){
    if(resact.authorRole === role && resact.authorEstateClass === estate){
      if(resact.actionKind ===  "create-nature-reserve"){
        let cost = getEmbankmentCost(resact.managementUnit, gameState) / 4; // idk how much this should cost so itll be 1/4 of the embankment cost cuz it DOES NOTHING
        muBalances[resact.managementUnit].push(balanceChange(-cost, "Created a Nature Reserve at MU #" + resact.managementUnit));
      }
    }
  }

  let totalGrazingValue = [0,0,0,0] ;
  // convert partial balances to full balances
  for(let i = 0; i < muPartialBalances.length; i++){
    if (muPartialBalances[i].balanceFromClearing != 0){
      muBalances[i].push(balanceChange(muPartialBalances[i].balanceFromClearing, `Cleared ${muPartialBalances[i].clearedCells} cells`));
    }
    if (muPartialBalances[i].balanceFromCutting != 0){
      muBalances[i].push(balanceChange(muPartialBalances[i].balanceFromCutting, `Cut reeds from ${muPartialBalances[i].cutCells} cells`));
    }
    if (muPartialBalances[i].balanceFromHunting != 0){
      muBalances[i].push(balanceChange(muPartialBalances[i].balanceFromHunting, `Hunted ${muPartialBalances[i].ducksHunted} ducks over ${muPartialBalances[i].huntedCells} cells`));
    }
    // add grazing value
    for(let j = 0; j < totalGrazingValue.length; j++){
      totalGrazingValue[j] += muPartialBalances[i].grazedValue[j];
    }
  }
  let fodderCost = 0
  // finally we can calculate grazing cost
  if(newHerdSize > 0){ // if they have at least one cow
    const feedPrice = constants.getRoleConstants("Stockbreeder", "public")?.feedCost || 10;
    let feedlessCowSeasons = 0;
    for(let i = 0; i < totalGrazingValue.length; i++){
      let currentSeasonAnimalCapacity = totalGrazingValue[i];
      feedlessCowSeasons += Math.max(newHerdSize - currentSeasonAnimalCapacity, 0);
    };
    fodderCost = feedlessCowSeasons * feedPrice;
    metaTransfer.push(balanceChange(-fodderCost, `Bought fodder for ${feedlessCowSeasons} cow-seasons`));
    
    // cow revenue
    const cowProductivity = constants.getRoleConstants("Stockbreeder", "public")?.animalProductivity || 400;
    metaTransfer.push(balanceChange(newHerdSize * cowProductivity, "Cows produced value"));
 }

 // calculate aggrigate balance change
 let aggrigate = 0;

 for(let bc of metaTransfer){
  aggrigate += bc.balance;
 }
 for(let mu of muBalances){
  for(let bc of mu){
    aggrigate += bc.balance;
  }
 }
 
 let agCost:AggrigateCosts = {
  totalBalanceChange:aggrigate,
  role:role,
  estate:estate,
  startingBalance: roleGroup.money,
  metaBalanceChanges:metaTransfer,
  MUBalanceChanges:muBalances,
  huntingBag:totalHuntingBag,
  fodderCost:fodderCost,
  newHerdSize:newHerdSize,
 }
 return agCost;
}


export function getTotalVotes(votes:Map<number,number>){
  let voteArray = new Array(sim.WATER_LEVEL_MANAGEMENT_SCHEMES.length).fill(0);
  for(let [key, value] of votes){
    voteArray[value]++;
  }
  return voteArray;
}

export function getVoteWinner(votes:Map<number,number>){
  let v = getTotalVotes(votes);
  let mostVotes = 0;
  for(let i = 0; i < v.length; i++){
    if(v.length == 1) break;
    if(v[i] > v[i-1]) mostVotes = i;
  }
  return mostVotes;
}

// creates a cloned cell for testing values etc
function initTestCell(x:number, y:number, gameState:GameState, grid:sim.WetlandGrid) : sim.Cell{
  let cellData = structuredClone(gameState.cellData[x][y]); // once again, we avoid any overwrite shannanigans
  let cell = new sim.Cell(x,y, cellData.elevation, cellData.landType, cellData.managementUnit, grid);
  cell.initWithInterface(cellData);
  return cell;
}
export function getNatureReserveCost(muNumber:number, gameState:GameState){
  return getEmbankmentCost(muNumber, gameState) / 4;
}

// in theory we would only need to use the linear function for cost
// however, one day we might need to update the function to keep the cell types in mind.
// therefore, we construct the actual WM and run the cost function on that.
// IF THE OUTPUT IS NEGATIVE, THAT MEANS IT IS IMPOSSIBLE TO EMBANK because it's on the lake
export function getEmbankmentCost(muNumber:number, gameState:GameState){
  // constructors require a grid. this does not matter.
  let grid = new sim.WetlandGrid(gameState.gridData.sizex, gameState.gridData.sizey); 

  // get all cells in the data that are a part of the mu.
  let cells = [];
  let mu = new sim.ManagementUnit(muNumber, grid);
  for(let x = 0; x < gameState.gridData.sizex; x++){
    for(let y = 0; y < gameState.gridData.sizey; y++){
      if (gameState.cellData[x][y].managementUnit === muNumber){
        let cel = initTestCell(x,y, gameState, grid);
        cells.push(cel);
        mu.registerCell(cel); // this is important because it adds the cell and updates the flood source variable
      }
    }
  }
  return mu.calculateEmbankmentCost();
}
// disembankments cost one half of the cost of embankment
export function getDisembankmentCost(muNumber:number, gameState:GameState){
  return getEmbankmentCost(muNumber, gameState) / 2;
}

// negative means it adds money, positive means it costs money. 
// a return of null means that it is impossible.
// note that you need to provide the wl
// default climate variability is the neutral one. Only change this if you are using this function to calculate the actual final state, which should not be done here IMO
export function getCellActionCost(action:number, x:number, y:number, gameState:GameState, wlRegime:number[], climateVariability = sim.FLORA_CLIMATE_VARIABILITY[2]){

  let activeWlRegime = wlRegime;
  // todo: use the WLregims of the MU if it is embanked
  let grid = new sim.WetlandGrid(gameState.gridData.sizex, gameState.gridData.sizey); 
  let testCell = initTestCell(x,y, gameState, grid);
  if(gameState.muData[testCell.managementUnit].embanked){ // if embanked, use the embanked wm regime
    activeWlRegime = structuredClone(gameState.muData[testCell.managementUnit].wmRegime);
  }
  switch (action){
    case sim.NO_ACTION:
      return 0; // no action means no cost! wow!
    case sim.GRAZING:
      return 0; // grazing costs are not done on a cell-by-cell basis
    case sim.CLEARING:
      let cCost = testCell.getClearingCost();
      if(cCost < 0){
        return null;
      }
      else return cCost;
    case sim.CUT:
      let cRev = testCell.getCuttingRevenue(activeWlRegime, climateVariability);
      if(cRev < 0) return null;
      return -1 *cRev  // this also takes into account climate variability, but they cant know what that is so...
  }

}
// returns the grazing value for the cell. its in the form of an array of 4 numbers, being the value per season. This shoud be used to display the usefulness of grazing to the player. 
//        might wanna do like a heatmap instead of numbers instead of straight displaying these values. 4 values per cell is a lot. maybe sum them and display a total value?
export function getGrazingValue(x:number, y:number, gameState:GameState, wlRegime:number[], climateVariability = sim.FLORA_CLIMATE_VARIABILITY[2]) : number[]{
  let activeWlRegime = wlRegime;
  let grid = new sim.WetlandGrid(gameState.gridData.sizex, gameState.gridData.sizey); 
  let testCell = initTestCell(x,y, gameState, grid);
  if(gameState.muData[testCell.managementUnit].embanked){ // if embanked, use the embanked wm regime
    activeWlRegime = structuredClone(gameState.muData[testCell.managementUnit].wmRegime);
  }
  return testCell.getGrazingValue(activeWlRegime, climateVariability);
}

// calculates the total grazing cost for a stockbreeder.
// you need an array of seasonal grazing values. You can just make an array from the outputs of getGrazing Value.
// the herdsize is the size of the herd of the player doing the grazing
export function getTotalGrazingCost(grazingValues:number[][], herdsize:number){
  let totals = [0,0,0,0];
  for(let gv of grazingValues){
    for(let i = 0; i < gv.length; i ++){
      totals[i] += gv[i];
    }
  }
  let totalCost = 0;
  const feedPrice = constants.getRoleConstants("Stockbreeder", "public")?.feedCost || 10;
  for(let seasonTotal of totals){
    totalCost += Math.max(herdsize - seasonTotal, 0) * feedPrice; // each unfed animal costs 10 per season that they are unfed
  }
  return totalCost;
}
export function getHuntingBag(x:number, y:number, gameState:GameState, wlRegime:number[], climateVariability = sim.FAUNA_CLIMATE_VARIABILITY[2], faunaName = "DUCK"){
  let grid = new sim.WetlandGrid(gameState.gridData.sizex, gameState.gridData.sizey); 
  // todo: select active regime of wm if embanked
  let activeWlRegime = wlRegime;
  let cell = initTestCell(x,y, gameState, grid);
  if(gameState.muData[cell.managementUnit].embanked){ // if embanked, use the embanked wm regime
    activeWlRegime = structuredClone(gameState.muData[cell.managementUnit].wmRegime);
  }
  let huntingBag = cell.getPotentialHuntingBag(activeWlRegime, climateVariability, faunaName);
  return huntingBag;
}

export function getCellFaunaCapacity(x:number, y:number, gameState:GameState, wlRegime:number[], faunaName = "DUCK"){
  let grid = new sim.WetlandGrid(gameState.gridData.sizex, gameState.gridData.sizey); 
  // todo: select active regime of wm if embanked
  let activeWlRegime = wlRegime;
  let cell = initTestCell(x,y, gameState, grid);
  if(gameState.muData[cell.managementUnit].embanked){ // if embanked, use the embanked wm regime
    activeWlRegime = structuredClone(gameState.muData[cell.managementUnit].wmRegime);
  }
  let duckCapacity = cell.getFaunaCapacity(activeWlRegime, faunaName);
  return duckCapacity;
}

// public and private hunters have very different math for their profits
// private hunters get paid per cell, public do NOT
export function getPrivateHunterCellProfit(x:number, y:number, gameState:GameState, wlRegime:number[], climateVariability = sim.FAUNA_CLIMATE_VARIABILITY[2], faunaName = "DUCK"){
  let huntingBag = getHuntingBag(x,y, gameState, wlRegime, climateVariability, faunaName);
  // the private hunter uses this nebulous function : (((huntingbag * 141,78)+107672)/6.5597) 
  // however, this is absurd and not applicable to cell-by-cell. also its in 2000AD dollars but we are 2024. For this reason its just 30 bucks a duck.
  let duckValue = constants.getRoleConstants("Hunter", "private")?.valuePerDuck || 0;
  let turnOver = huntingBag * duckValue;
  //let turnOver = ((((huntingBag * 141.78)) / 6.5597));
  return turnOver;
}

// public hunters do NOT get paid per cell. instead its based on the total hunting bag.
// you can just run getHuntingBag on each cell they hunted, and put the total into this function to get their profits.
export function getPublicHunterProfit(huntingBag: number) : number{
  if(huntingBag <= 100){
    return 500;
  }
  if(huntingBag > 100 && huntingBag <= 150){
    return 800;
  }
  if(huntingBag > 150){
    return 1200;
  }
  else return 0;

}

export function getWaterManagementSchemeConstant(wm: number) {
  if (wm < 1 || wm > 5) {
    return null;
  }
  return sim.WATER_LEVEL_MANAGEMENT_SCHEMES[wm];
}

export function createGameActionsFromProposal(proposal: Proposal, wm: number): GameActions {
  const newGameActions: GameActions = {
    cellActions: [],
    faunaActions: [],
    muActions: [],
    wmRegime: sim.WATER_LEVEL_MANAGEMENT_SCHEMES[wm],
  }

  for (let i=0; i < proposal.mapActions.length; i ++) {
    const mapAc = proposal.mapActions[i]
    const simType = constants.actions[mapAc.actionKind]?.simType;
    if (typeof simType === "number") {
      newGameActions.cellActions.push([
        simType,
        mapAc.location.x,
        mapAc.location.y,
        mapAc.authorId,
      ])
    }
  }


  for (let i=0; i < proposal.faunaActions.length; i++) {
    const faunaAc = proposal.faunaActions[i];
    newGameActions.faunaActions.push([
      faunaAc.faunaName,
      faunaAc.location.x,
      faunaAc.location.y,
      faunaAc.playerId,
    ]);
  }

  for (let i=0; i < proposal.muActions.length; i++) {
    const muAc = proposal.muActions[i]
    const simType = constants.actions[muAc.actionKind]?.simType;
    if (typeof simType === "number") {
      newGameActions.muActions.push([
        simType,
        muAc.managementUnit,
        muAc.wmRegime ?? null,
      ]);
    }
  }

  for (let i=0; i < proposal.reservationMuActions.length; i++) {
    const muAc = proposal.reservationMuActions[i]
    const simType = constants.actions[muAc.actionKind]?.simType;
    if (typeof simType === "number") {
      newGameActions.muActions.push([
        simType,
        muAc.managementUnit,
        muAc?.wmRegime || null,
      ]);
    }
  }
  console.log(newGameActions);

  return newGameActions;
}

export function startGame( gameRoom: GameRoom, players: Map<number, Player>, jsonData:any) {
  gameRoom.gameState = getInitialGameState(jsonData); //  can plug in file name and alternative fauna here. 

  const playerIdArray = Array.from(gameRoom.joinedPlayers ?? new Set<number>());
  // Loop through players and set the Role Groups
  for (let i=0; i< playerIdArray.length; i++) {
    const ply = players.get(playerIdArray[i]);

    if (!ply) {
      continue;
    }

    if (!gameRoom.groups.get(`${ply.estate}-${ply.role}`)) {
      const roleData = constants.allRoles[ constants.allRoles.findIndex((elem) => {
        return elem.role === ply.role && elem.estate === ply.estate;
      }) ];

      // Set the inital conditions for that role
      const defaultRoleGroupData : RoleGroup = {
        money: roleData?.initialMoney || 0,
        startingRoleData: roleData,
        totalHuntingBag: 0,// hunting bag starts at zero
        herdSize: roleData?.initialHerdSize || 0,
        subsidy:roleData?.initialSubsidies || 0,
      }
      gameRoom.groups.set(`${ply.estate}-${ply.role}`, defaultRoleGroupData)
    }
  }
}

export function getNextGameState(state:GameState, actions:GameActions): GameState {
  /* new funcs testing zone */
  // console.log("+++++++++++ NEW FUNCS TESTING ++++++++++");
  // let x = 0;
  // let y = 0;
  // let wl = sim.WATER_LEVEL_MANAGEMENT_SCHEMES[4];
  // console.log("TEST CELL INFO : ", state.cellData[x][y]);
  // console.log("GET EMBANKMENT COST : ",getEmbankmentCost(4, state));
  // console.log("ACTION COST no action: ",getCellActionCost(sim.NO_ACTION, x, y, state, wl,));
  // console.log("ACTION COST grazing  : ",getCellActionCost(sim.GRAZING, x, y, state, wl,));
  // console.log("ACTION COST clearing : ",getCellActionCost(sim.CLEARING, x, y, state, wl,));
  // console.log("ACTION COST cutting  : ",getCellActionCost(sim.CUT, x, y, state, wl,));
  // console.log("GET GRAZING VALUE : ", getGrazingValue(x,y, state, wl));
  // console.log("GET TOTAL GRAZING COST : ", getTotalGrazingCost([[10,10,5,5],[5,5,10,10]], 50));
  // console.log("GET HUNTING BAG : ", getHuntingBag(x,y,state,wl));
  // console.log("GET PRIVATE HUNTER REV (cell) : ", getPrivateHunterCellProfit(x,y,state,wl));
  // console.log("GET PUBLIC HUNTER REV : ", getPublicHunterProfit(700));
  // console.log("+++++++++++ END NEW FUNCS TESTING ++++++++++");

  /* end new funcs testing zone */
  // console.log("first game state: ", state.cellData[0][5].actionLog);
  const simulation = new sim.WetlandSimulation(state.gridData.sizex, state.gridData.sizex);
  simulation.inputGameState(state.cellData, state.muData, state.gridData, state.simData);
  simulation.inputActions(actions.cellActions, actions.faunaActions, actions.muActions, actions.wmRegime);
  simulation.advanceYear();
  const outputData = simulation.outputGameState();
  const gameData = rawSimOutputToGameState(outputData);
  const balanceChanges = outputData[4]; // this is where stuff like changes of user balances might go
  // console.log("end game state: ", state.cellData[0][5].actionLog);
  // console.log("game data diff:", gameData.cellData[0][5].actionLog);
  return gameData;
}

export function getInitialGameState(jsonData:any, fauna = sim.DEFAULT_FAUNA){
  let simulation = new sim.WetlandSimulation(0,0, fauna);
  simulation.loadFile(jsonData);
  let outputData = simulation.outputGameState();
  return rawSimOutputToGameState(outputData);
}

function rawSimOutputToGameState(outputData: any[]): GameState{
  let gameData: GameState = {
    cellData:outputData[0],
    muData:outputData[1],
    gridData:outputData[2],
    simData:outputData[3]
  }
  return gameData;
}

// interpolates between two colors
export function mapNumberToColor(num: number, minRange: number, maxRange : number, startColor : string, endColor:string, forceInitialColor: string|null = null) {
  // Clamp the input number to ensure it's within the given range
  const clampedNum = Math.max(minRange, Math.min(maxRange, num));
  // Convert hex start and end colors to RGB
  const startRGB = hexToRgb(startColor);
  const endRGB = hexToRgb(endColor);

  if (forceInitialColor && clampedNum == minRange){
    return forceInitialColor;
  }
  // Normalize the input number between 0 and 1
  const normalized = (clampedNum - minRange) / (maxRange - minRange);
  // Interpolate between the start and end colors
  const interpolatedRGB = [
      Math.round(startRGB[0] + normalized * (endRGB[0] - startRGB[0])),
      Math.round(startRGB[1] + normalized * (endRGB[1] - startRGB[1])),
      Math.round(startRGB[2] + normalized * (endRGB[2] - startRGB[2]))
  ];
  // Convert interpolated RGB back to hex
  return rgbToHex(interpolatedRGB[0], interpolatedRGB[1], interpolatedRGB[2]);
}
// Helper function to convert hex color to RGB
function hexToRgb(hex: string) {
  // Remove the leading hash (#) if present
  hex = hex.replace(/^#/, '');
  // Parse the r, g, b values from the hex string
  let bigint = parseInt(hex, 16);
  let r = (bigint >> 16) & 255;
  let g = (bigint >> 8) & 255;
  let b = bigint & 255;
  return [r, g, b];
}
// Helper function to convert RGB to Hex
function rgbToHex(r:number, g:number, b:number) {
  const toHex = (c: number) => {
      const hex = c.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
